import { getDb, STORE_PENDING_VISITAS } from './db';
import {
  claveDeMercado, type CategoriaProducto, type VisitaParaRegistrar,
} from '../domain/levantamiento';

/**
 * Cola local de visitas del levantamiento de canal.
 *
 * POR QUÉ EXISTE: **dentro de los mercados no hay cobertura**. No es un caso raro que haya que
 * contemplar, es la condición normal de trabajo — el documento lo pone como primer riesgo. Si
 * medir un puesto exigiera conexión, no se levantaría un solo dato.
 *
 * POR QUÉ ES UN STORE APARTE: mismo razonamiento que la cola de canjes. La de asistencia es el
 * camino más crítico del sistema y ya está en producción con gente marcando de verdad;
 * generalizarla para una tercera forma de dato es arriesgar el marcado para ahorrar un archivo.
 *
 * ADEMÁS DE COLA, ES EL HISTORIAL DEL DÍA (cambio del 2026-08-19). Antes la ficha se BORRABA en
 * cuanto lograba subir, y por eso el auditor no tenía forma de saber por qué puestos ya había
 * pasado: apenas aparecía señal, lo registrado desaparecía del celular. Se comprobó contra
 * producción qué produce eso — puestos cargados dos veces y numeración incoherente (en Unicachi
 * Norte hay dos puestos con el número 1). Ahora la ficha enviada se CONSERVA marcada como
 * `enviada`, que es lo que permite armar la lista de avance sin señal, que es la condición normal
 * adentro del mercado. Lo que sí se suelta al enviar son los `Blob` de las fotos: ver
 * `flushVisitas` y `purgarVisitasEnviadas`.
 */

/** La visita tal como se guarda localmente: sin las rutas, que salen al subir las fotos. */
export interface VisitaEnCola extends VisitaParaRegistrar {
  clientUuid: string;
  campaignId: string;
  /** Vacío cuando el mercado se escribió a mano; entonces manda `mercadoLibre`. */
  pdvId: string;
  mercadoLibre: string;
  /** `null` = ese día no tenía programación ahí. El servidor lo marca, no lo rechaza. */
  assignmentId: string | null;
  fecha: string;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
}

/**
 * Las fotos de UNA clave, en el orden en que el auditor las tomó.
 *
 * EL ORDEN NO ES DECORATIVO: la ruta de cada foto lleva su índice dentro de la clave
 * (`form-photos/visita-<uuid>-<clave>-<n>.jpg`), así que el orden es (1) lo que el panel muestra,
 * y (2) lo que hace que un reintento escriba en el MISMO path en vez de dejar copias sueltas en
 * el Storage. Por eso se suben una por una y no con `Promise.all`.
 */
export interface FotosDeClave {
  clave: string;
  blobs: Blob[];
}

/**
 * Una visita esperando, con TODAS sus fotos.
 *
 * Antes eran dos campos fijos (`puestoBlob`, `hallazgoBlob`), después una foto por clave, y desde
 * el 2026-08-19 VARIAS por clave: el usuario pidió poder subir más de una foto en todos los
 * campos que aceptan foto. La clave es con la que el servidor las amarra a su fila (el precio de
 * una presentación, una promoción, el hallazgo), así que sin ella la evidencia del precio —el
 * dato que el cliente discute— quedaría colgando sin decir de cuál fila es.
 */
export interface PendingVisita {
  clientUuid: string;
  input: VisitaEnCola;
  fotos: FotosDeClave[];
}

/**
 * En qué punto del envío está la ficha.
 *
 * `enviada` es la que agregó el cambio del 19-ago: el servidor ya la aceptó y ya tiene sus fotos,
 * pero el registro se queda en el celular para poder pintar el avance del día sin señal. **Nunca
 * se reintenta** (ver `flushVisitas`) y se purga por antigüedad (ver `purgarVisitasEnviadas`).
 */
export type EstadoEnvioVisita = 'pendiente' | 'enviada' | 'fallido';

export interface QueuedVisita extends PendingVisita {
  attempts: number;
  lastError: string | null;
  status: EstadoEnvioVisita;
  /** Cuándo se guardó en el celular (epoch ms). Es la hora que ve el auditor en su lista. */
  guardadaEn: number;
  /** Cuándo la aceptó el servidor (epoch ms). `null` mientras no esté `enviada`. */
  enviadaEn: number | null;
}

/** Rechazos definitivos consecutivos tras los cuales la visita pasa a `fallido`. */
export const MAX_VISITA_ATTEMPTS = 5;

/**
 * Cuántos días se conserva una ficha YA ENVIADA antes de borrarla del celular.
 *
 * POR QUÉ 7 Y NO 1 NI 30. Una semana es margen de sobra para que un celular que estuvo sin señal
 * termine de sincronizar, y purgar antes de eso obligaría a la pantalla a depender del servidor
 * justo en los días en que no lo alcanza. Más allá de una semana no aporta: desde el cambio del
 * 19-ago la lista de avance se arma con lo que devuelve el servidor más lo que acá quede sin
 * subir, así que borrar una `enviada` vieja NO le quita nada a la pantalla — la sigue trayendo el
 * servidor, que es quien tiene el dato bueno y quien lo guarda para siempre.
 *
 * POR QUÉ HAY QUE PURGAR. Lo que pesa son los `Blob` de las fotos —y esos se sueltan apenas la
 * ficha se envía, porque el servidor ya las tiene—, pero el registro igual ocupa. Un mes de
 * mercados a treinta puestos por día llena el almacenamiento del navegador, y cuando se llena el
 * navegador **empieza a rechazar las escrituras**: eso se manifestaría como «no puedo guardar la
 * ficha», de pie en un mercado, sin señal y sin ninguna otra copia del dato. Es el peor momento
 * posible para quedarse sin espacio.
 */
export const DIAS_RETENCION_ENVIADAS = 7;

const MS_RETENCION_ENVIADAS = DIAS_RETENCION_ENVIADAS * 24 * 60 * 60 * 1000;

/** Puerto de envío. Lo implementa `api/levantamiento.ts`, que se registra al importarse. */
export interface VisitaUploader {
  /**
   * Sube UNA foto. `indice` es su posición dentro de su clave y forma parte de la ruta: sin él,
   * la segunda foto de la misma clave pisaría a la primera (mismo path, `upsert: true`).
   */
  uploadVisitaPhoto(
    clientUuid: string, clave: string, indice: number, photo: Blob,
  ): Promise<string>;
  /** `rutas` mapea la clave a las rutas de SUS fotos, en el mismo orden que se tomaron. */
  registrarVisita(input: VisitaEnCola, rutas: Record<string, string[]>): Promise<string>;
  isPermanentError(err: unknown): boolean;
}

let uploader: VisitaUploader | null = null;

export function setVisitaUploader(u: VisitaUploader): void {
  uploader = u;
}

/** Forma en que se guardaban las fotos antes del 2026-08-19: una sola por clave. */
interface FotoDeClaveVieja {
  clave: string;
  blob: Blob;
}

/**
 * Deja las fotos en la forma nueva (`{ clave, blobs }`), venga lo que venga.
 *
 * QUÉ ERROR EVITA, Y NO ES TEÓRICO: un celular puede estar hoy con fichas pendientes guardadas en
 * la forma vieja y actualizar la app mañana. Si al subirlas se leyera `f.blobs` a secas, esas
 * fichas se enviarían **sin ninguna foto** —o reventarían al recorrer `undefined`— y su única
 * copia era esa. La forma vieja se convierte a una lista de un elemento; claves repetidas se
 * agrupan conservando el orden de aparición, que es el orden en que se tomaron.
 */
function fotosNormalizadas(fotos: (FotosDeClave | FotoDeClaveVieja)[] | undefined): FotosDeClave[] {
  const out: FotosDeClave[] = [];
  const agrega = (clave: string, blobs: Blob[]): void => {
    const ya = out.find((o) => o.clave === clave);
    if (ya) ya.blobs.push(...blobs);
    else out.push({ clave, blobs: [...blobs] });
  };
  for (const f of fotos ?? []) {
    const nuevas = (f as FotosDeClave).blobs;
    if (Array.isArray(nuevas)) agrega(f.clave, nuevas);
    else if ((f as FotoDeClaveVieja).blob) agrega(f.clave, [(f as FotoDeClaveVieja).blob]);
  }
  return out;
}

/**
 * Una ficha que todavía no se subió: es la única que se reintenta.
 *
 * Se pregunta por `=== 'pendiente'` y no por `!== 'fallido'` a propósito. Con la forma negativa,
 * el estado `enviada` que se agregó el 19-ago habría entrado sola en la lista de reintentos.
 * El `?? 'pendiente'` cubre un registro guardado por una versión anterior al campo: sin estado
 * conocido, lo seguro es tratarlo como no subido, porque el dato solo está acá.
 */
function esPendiente(v: QueuedVisita): boolean {
  return (v.status ?? 'pendiente') === 'pendiente';
}

/** Guarda una visita en la cola. Idempotente por `clientUuid`: conserva su historial. */
export async function enqueueVisita(p: PendingVisita): Promise<void> {
  const db = await getDb();
  const existing = await db.get(STORE_PENDING_VISITAS, p.clientUuid);
  await db.put(STORE_PENDING_VISITAS, {
    ...p,
    fotos: fotosNormalizadas(p.fotos),
    attempts: existing?.attempts ?? 0,
    lastError: existing?.lastError ?? null,
    status: existing?.status ?? 'pendiente',
    // Se conserva la hora del PRIMER guardado: es cuando se midió el puesto, y es lo que ordena
    // la lista de avance. Reescribirla al corregir una ficha la haría saltar al final.
    guardadaEn: existing?.guardadaEn ?? Date.now(),
    enviadaEn: existing?.enviadaEn ?? null,
  });
}

export async function listPendingVisitas(): Promise<QueuedVisita[]> {
  const db = await getDb();
  return (await db.getAll(STORE_PENDING_VISITAS)).filter(esPendiente);
}

export async function listFailedVisitas(): Promise<QueuedVisita[]> {
  const db = await getDb();
  return (await db.getAll(STORE_PENDING_VISITAS)).filter((i) => i.status === 'fallido');
}

/**
 * Devuelve a la cola las fichas que se dieron por perdidas, para que el próximo envío las reintente.
 *
 * ## Por qué hace falta, con números
 *
 * `flushVisitas` salta todo lo que no está `pendiente`, así que una ficha en `fallido` **no se
 * reintenta nunca más**: se queda en el celular sin que nadie lo sepa. Y a `fallido` se llega por
 * un camino que resultó ser frecuente — un rechazo con código PERMANENTE gasta los 5 intentos de
 * una, y `23502` (columna NOT NULL) está en esa lista.
 *
 * Eso es exactamente lo que pasaba: hasta la `0060`, `levantamiento_precios.marca_id` era NOT NULL
 * y la ficha con una marca ESCRITA A MANO —la fila «Marca económica», o cualquier marca fuera de la
 * cuadrícula— reventaba con `23502` **después de haber subido sus fotos**. Medido contra producción
 * el 2026-08-22: **184 fotos en el bucket sin ninguna fila que las apunte, de 46 fichas que nunca
 * llegaron** (9 del 19-ago, 25 del 21-ago, 12 de ese mismo día). Treinta y siete traían foto de
 * puesto, o sea que eran visitas medidas completas.
 *
 * La `0060` quitó la causa, así que lo que quedó en `pendiente` sube solo. Esto es para lo otro:
 * lo que ya se marcó `fallido` y, sin esta función, se perdía aunque el servidor ya lo aceptara.
 *
 * ## Lo que NO hace
 *
 * No borra, no reescribe la ficha y no toca `guardadaEn` —la hora en que se midió el puesto—. Y no
 * limpia `lastError`: si vuelve a fallar, el motivo anterior sigue a la vista, que es lo único que
 * permite distinguir «no había señal» de «el servidor lo rechaza por algo que sigue mal».
 *
 * Subir es idempotente por `client_uuid` (la RPC devuelve el id que ya existe), así que reintentar
 * una ficha que en realidad sí había entrado no duplica nada.
 */
export async function reencolarVisitasFallidas(): Promise<number> {
  const db = await getDb();
  const fallidas = (await db.getAll(STORE_PENDING_VISITAS)).filter((i) => i.status === 'fallido');
  for (const item of fallidas) {
    await db.put(STORE_PENDING_VISITAS, { ...item, status: 'pendiente', attempts: 0 });
  }
  return fallidas.length;
}

export async function pendingVisitaCount(): Promise<number> {
  return (await listPendingVisitas()).length;
}

export async function discardPendingVisita(clientUuid: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_PENDING_VISITAS, clientUuid);
}

/**
 * Lo mínimo para reconocer un puesto en la lista de avance del mercado.
 *
 * No lleva la ficha entera a propósito: la pantalla solo necesita distinguir un puesto de otro y
 * saber si ya subió. Las respuestas del cuestionario se leen del servidor, que es donde viven.
 */
export interface ResumenVisitaLocal {
  clientUuid: string;
  /** El número REAL del puesto en el mercado (el del letrero), no un correlativo del recorrido. */
  numeroRecorrido: number;
  /** Referencia libre para reconocerlo («el de doña Rosa»). Opcional: casi siempre viene vacía. */
  nombrePuesto: string | null;
  estado: 'medido' | 'no_pude';
  /**
   * Qué declaró que vende ESE puesto. La lista la usa el atajo «lo mismo que el puesto anterior»
   * de la ficha: en producción, **73 % de los puestos declaran lo mismo que el anterior** del mismo
   * mercado y día (91 de 124 medidos con puesto previo, medido el 2026-08-22), así que volver a
   * marcarlo casilla por casilla es el trabajo que ese atajo ahorra.
   *
   * Puede llegar vacía y es un valor legítimo, no un olvido: una visita `no_pude` no declara nada,
   * y las fichas guardadas en el celular antes de que este campo existiera tampoco lo traen — de
   * ahí el `?? []` al leerla.
   */
  categorias: CategoriaProducto[];
  /** Epoch ms. La pantalla la formatea; la hora se muestra en America/Lima. */
  guardadaEn: number;
  /** Mismos valores que el store: la pantalla no necesita un segundo vocabulario. */
  envio: EstadoEnvioVisita;
}

/**
 * Lo que este celular tiene registrado de un mercado en un día, subido o no.
 *
 * ES **LA MITAD** DE LA VISTA QUE PIDIÓ EL USUARIO —*«una vista donde pueda ir viendo cuántos
 * puestos va y cuáles eran»*—, y hay que tener claro cuál mitad. La pantalla (`FichaPuesto`) une
 * lo que devuelve el servidor con lo que sale de acá, y **el servidor es la fuente de verdad**:
 * es el único que ve lo registrado desde otro celular o en un día ya purgado de este. Lo que
 * aporta esta función y no aporta ninguna otra es **lo que todavía no subió**, que adentro del
 * mercado sin señal es todo el trabajo de la jornada. Armar la lista solo con esto fue el error
 * del 19-ago: desde otra máquina decía «todavía no registraste ningún puesto» con 22 visitas
 * cargadas.
 *
 * Devuelve también las `enviada` que aún no se purgaron, y eso importa: cuando no se le puede
 * preguntar al servidor son lo único que queda para pintar lo que ya subió hoy.
 *
 * Ordenada por hora de guardado (el orden en que el auditor recorrió), con el `clientUuid` como
 * desempate para que dos fichas guardadas en el mismo milisegundo no bailen entre renders.
 */
export async function visitasLocalesDe(
  claveMercado: string, fecha: string,
): Promise<ResumenVisitaLocal[]> {
  const db = await getDb();
  const todas = await db.getAll(STORE_PENDING_VISITAS);
  return todas
    .filter((v) => claveDeMercado(v.input.pdvId, v.input.mercadoLibre ?? '') === claveMercado
      && v.input.fecha === fecha)
    .map((v) => {
      // El `?? ''` NO es por el tipo —`nombrePuesto` es `string | null` en el dominio, nunca
      // `undefined`— sino por los registros que ya estaban guardados en el celular antes de que
      // el campo existiera: ahí sí llega sin la clave, y sin esto la lista reventaría al leerlo.
      const nombre = (v.input.nombrePuesto ?? '').trim();
      return {
        clientUuid: v.clientUuid,
        numeroRecorrido: v.input.numeroRecorrido,
        nombrePuesto: nombre === '' ? null : nombre,
        estado: v.input.estado,
        categorias: v.input.categorias ?? [],
        guardadaEn: v.guardadaEn ?? 0,
        envio: v.status ?? 'pendiente',
      };
    })
    .sort((a, b) => a.guardadaEn - b.guardadaEn || a.clientUuid.localeCompare(b.clientUuid));
}

/**
 * El número de puesto más alto registrado en este celular para ese mercado y día.
 *
 * ⚠️ YA NO SE USA PARA AUTOINCREMENTAR, que era su único motivo de existir. El 19-ago se confirmó
 * que `numeroRecorrido` es el número REAL del puesto dentro del mercado —el del letrero— y no un
 * correlativo del recorrido: contra producción, ese día en Mercado Naranjal se registraron 23,
 * 26, 3, 11 y 16 en ese orden de reloj. Precargar «el último + 1» proponía un número casi siempre
 * equivocado, y basta que al auditor se le olvide corregirlo UNA vez para que quede registrado un
 * puesto que no es — un número inventado no se distingue de uno real al leer la base.
 *
 * TAMPOCO LA USA YA EL AVISO de «este número ya lo usaste»: esa bandera pasó a mirar la unión de
 * lo que devuelve el servidor y lo que queda en esta cola (ver `unirAvance` en `FichaPuesto`),
 * porque mirando solo el celular no detectaba un puesto medido desde otro equipo — y en
 * producción hay repetidos que por eso nunca se avisaron. Queda sin llamadores fuera de su prueba;
 * si algún día estorba, borrarla no le quita nada a ninguna pantalla.
 *
 * Desde el cambio de la cola ve TAMBIÉN las fichas ya subidas, no solo las que esperan señal.
 */
export async function ultimoRecorridoLocal(clave: string, fecha: string): Promise<number> {
  const registradas = await visitasLocalesDe(clave, fecha);
  return registradas.reduce((max, v) => Math.max(max, v.numeroRecorrido), 0);
}

/**
 * Borra del celular las fichas ENVIADAS que ya cumplieron `DIAS_RETENCION_ENVIADAS`.
 *
 * ⚠️ SOLO TOCA LAS `enviada`, y esa es la regla que no se puede relajar nunca: una `pendiente` o
 * una `fallido` guarda la ÚNICA copia del dato —el servidor no la tiene— así que por vieja que
 * sea se queda. Una ficha lleva días sin subir justamente cuando la persona estuvo sin señal, que
 * es el caso que esta cola existe para proteger; borrarla por antigüedad sería tirar el trabajo
 * de un día entero y además hacerlo en silencio.
 *
 * Devuelve cuántas borró. `ahora` se puede inyectar para poder probar el plazo sin esperar días.
 */
export async function purgarVisitasEnviadas(ahora: number = Date.now()): Promise<number> {
  const db = await getDb();
  const todas = await db.getAll(STORE_PENDING_VISITAS);
  let borradas = 0;
  for (const v of todas) {
    if (v.status !== 'enviada') continue;
    // `enviadaEn` siempre existe en una enviada; los respaldos son por si un registro viejo
    // quedó sin marca de tiempo, y en ese caso conviene que se vaya: el servidor ya lo tiene.
    const marca = v.enviadaEn ?? v.guardadaEn ?? 0;
    if (ahora - marca < MS_RETENCION_ENVIADAS) continue;
    await db.delete(STORE_PENDING_VISITAS, v.clientUuid);
    borradas += 1;
  }
  return borradas;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Sube las visitas pendientes: primero TODAS sus fotos al Storage, después la RPC con las rutas.
 *
 * El orden importa: llamar la RPC antes de tener las rutas dejaría la visita apuntando a fotos
 * que no existen. La RPC es idempotente por `client_uuid`, así que reintentar cuando algunas
 * fotos ya habían subido no duplica nada — se vuelven a subir al mismo path con `upsert`.
 *
 * AL LOGRAR SUBIRLA NO SE BORRA: se marca `enviada` y **se sueltan los blobs**, que es lo único
 * que pesa de verdad. El registro sin fotos queda alimentando la lista de avance del día, y el
 * servidor —que ya tiene las fotos— es la copia buena.
 *
 * Una `enviada` NO SE REINTENTA JAMÁS. Si se colara en este bucle, la RPC no duplicaría nada
 * (es idempotente por `client_uuid`: devuelve el id que ya existe), pero **volvería a subir todas
 * sus fotos al Storage en cada intento**: megabytes por ficha, por el dato de una persona que
 * está en la calle pagando su plan de datos. Por eso se filtra al leer y se vuelve a comprobar
 * dentro del bucle.
 *
 * Un fallo de RED no consume intentos y los reinicia: estar sin señal en un mercado es la
 * condición normal, no un error de la persona. Solo los rechazos definitivos gastan los 5
 * intentos, y al agotarlos la visita sale de pendientes (se apaga el aviso eterno) y queda
 * visible como fallida con su motivo — nunca se borra sola.
 */
export async function flushVisitas(): Promise<{ sent: number; remaining: number }> {
  const db = await getDb();
  // Se purga acá y no en un temporizador aparte: `flushVisitas` corre cada vez que hay señal,
  // que es exactamente cuando aparecen enviadas nuevas. Va ANTES del corte por lista vacía
  // porque el estado normal del celular es «ya subió todo», y ahí también hay que limpiar.
  await purgarVisitasEnviadas();

  const items = (await db.getAll(STORE_PENDING_VISITAS)).filter(esPendiente);
  if (items.length === 0) return { sent: 0, remaining: 0 };
  if (!uploader) {
    throw new Error('La cola de visitas no tiene enviador registrado (importa api/levantamiento).');
  }

  let sent = 0;
  for (const item of items) {
    if (!esPendiente(item)) continue; // ver el encabezado: una enviada no se resube nunca
    try {
      const rutas: Record<string, string[]> = {};
      for (const grupo of fotosNormalizadas(item.fotos)) {
        // Una por una y en secuencia: con `Promise.all` las rutas volverían en el orden en que
        // conteste el Storage y el orden en que el auditor tomó las fotos —que es lo que el
        // panel muestra— se mezclaría. El índice va en la ruta, así que también dejaría de ser
        // estable entre reintentos.
        const deEstaClave: string[] = [];
        for (let i = 0; i < grupo.blobs.length; i += 1) {
          deEstaClave.push(
            await uploader.uploadVisitaPhoto(item.clientUuid, grupo.clave, i, grupo.blobs[i]),
          );
        }
        rutas[grupo.clave] = deEstaClave;
      }

      // `clientUuid` se fuerza desde la CLAVE del store, no del payload: es lo que hace
      // idempotente el reintento en el servidor. Si los dos valores pudieran divergir, un
      // reintento crearía una visita duplicada — y un puesto contado dos veces desbalancea la
      // comparación entre mercados, que es todo el valor del ejercicio.
      await uploader.registrarVisita({ ...item.input, clientUuid: item.clientUuid }, rutas);
      await db.put(STORE_PENDING_VISITAS, {
        ...item,
        // Los blobs se sueltan: el servidor ya tiene las fotos y acá solo ocuparían hasta la
        // purga. El resto del registro se queda, que es lo que pinta la lista de avance.
        fotos: [],
        // Una ficha encolada por una versión anterior no trae hora de guardado. Se le pone la
        // del envío —que es lo más cercano que hay— para que no aparezca en 1970 al principio
        // de la lista del día.
        guardadaEn: item.guardadaEn ?? Date.now(),
        lastError: null,
        status: 'enviada',
        enviadaEn: Date.now(),
      });
      sent += 1;
    } catch (err) {
      const permanent = uploader.isPermanentError(err);
      const attempts = permanent ? item.attempts + 1 : 0;
      await db.put(STORE_PENDING_VISITAS, {
        ...item,
        attempts,
        lastError: errorMessage(err),
        status: permanent && attempts >= MAX_VISITA_ATTEMPTS ? 'fallido' : 'pendiente',
      });
    }
  }

  const remaining = (await listPendingVisitas()).length;
  return { sent, remaining };
}
